import { FileUploader } from '@/components/FileUploader';
import { ApiError, getBranding, updateBranding } from '@/lib/api';
import type { BrandJson } from '@/lib/types';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WhyUsSlot {
  icon: string;
  title: string;
  body: string;
}

interface FormState {
  primaryColour: string;
  tagline: string;
  pitchParagraph: string;
  whyUs: [WhyUsSlot, WhyUsSlot, WhyUsSlot];
}

const EMPTY_SLOT: WhyUsSlot = { icon: 'star', title: '', body: '' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function slotsFromBrand(whyUs: BrandJson['whyUs']): [WhyUsSlot, WhyUsSlot, WhyUsSlot] {
  const filled = whyUs.slice(0, 3).map((e) => ({
    icon: e.icon || 'star',
    title: e.title,
    body: e.body,
  }));
  while (filled.length < 3) filled.push({ ...EMPTY_SLOT });
  return filled as [WhyUsSlot, WhyUsSlot, WhyUsSlot];
}

function formFromBrand(brand: BrandJson): FormState {
  return {
    primaryColour: brand.primaryColour ?? '#004AAD',
    tagline: brand.tagline ?? '',
    pitchParagraph: brand.pitchParagraph ?? '',
    whyUs: slotsFromBrand(brand.whyUs),
  };
}

function isDirty(form: FormState, brand: BrandJson): boolean {
  if (form.primaryColour !== (brand.primaryColour ?? '#004AAD')) return true;
  if (form.tagline !== (brand.tagline ?? '')) return true;
  if (form.pitchParagraph !== (brand.pitchParagraph ?? '')) return true;
  const slots = slotsFromBrand(brand.whyUs);
  for (let i = 0; i < 3; i++) {
    const formSlot = form.whyUs[i as 0 | 1 | 2];
    const savedSlot = slots[i as 0 | 1 | 2];
    if (formSlot.title !== savedSlot.title) return true;
    if (formSlot.body !== savedSlot.body) return true;
  }
  return false;
}

function buildPatch(form: FormState): Partial<BrandJson> {
  const whyUsFiltered = form.whyUs
    .filter((s) => s.title.trim().length > 0 || s.body.trim().length > 0)
    .map((s) => ({ icon: s.icon || 'star', title: s.title, body: s.body }));

  return {
    primaryColour: form.primaryColour || null,
    tagline: form.tagline.trim() || null,
    pitchParagraph: form.pitchParagraph.trim() || null,
    whyUs: whyUsFiltered,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BrandingSettings() {
  const [current, setCurrent] = useState<BrandJson | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>({
    primaryColour: '#004AAD',
    tagline: '',
    pitchParagraph: '',
    whyUs: [{ ...EMPTY_SLOT }, { ...EMPTY_SLOT }, { ...EMPTY_SLOT }],
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getBranding()
      .then((brand) => {
        setCurrent(brand);
        setForm(formFromBrand(brand));
      })
      .catch(() => {
        // leave defaults in place; non-fatal
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleReset = useCallback(() => {
    if (current) {
      setForm(formFromBrand(current));
      setError(null);
    }
  }, [current]);

  async function handleSave() {
    if (!current) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateBranding(buildPatch(form));
      setCurrent(updated);
      setForm(formFromBrand(updated));
      setSuccess(true);
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  function setWhyUsField(index: number, field: 'title' | 'body', value: string) {
    setForm((f) => {
      const next = f.whyUs.map((s, i) => (i === index ? { ...s, [field]: value } : s)) as [
        WhyUsSlot,
        WhyUsSlot,
        WhyUsSlot,
      ];
      return { ...f, whyUs: next };
    });
  }

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-48 animate-pulse rounded bg-secondary" />
        <div className="h-4 w-full animate-pulse rounded bg-secondary" />
        <div className="h-4 w-3/4 animate-pulse rounded bg-secondary" />
      </div>
    );
  }

  const dirty = current !== null && isDirty(form, current);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Branding</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Customise how your business appears on the customer portal.
        </p>
      </header>

      <div className="space-y-6">
        {/* Primary colour */}
        <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-sm font-medium">Brand colour</h2>
          </div>
          <div className="px-6 py-5">
            <label
              htmlFor="primary-colour-hex"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Primary colour
            </label>
            <div className="flex items-center gap-3">
              <input
                id="primary-colour-picker"
                type="color"
                value={form.primaryColour}
                onChange={(e) => {
                  setForm((f) => ({ ...f, primaryColour: e.target.value }));
                }}
                className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface p-0.5"
                aria-label="Pick primary colour"
              />
              <input
                id="primary-colour-hex"
                type="text"
                value={form.primaryColour}
                onChange={(e) => {
                  setForm((f) => ({ ...f, primaryColour: e.target.value }));
                }}
                maxLength={7}
                placeholder="#004AAD"
                className="w-32 rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Hex value, e.g. <span className="num">#004AAD</span>. Applied to the customer portal
              only — the staff interface is not re-coloured.
            </p>
          </div>
        </section>

        {/* Tagline + pitch */}
        <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-sm font-medium">Portal copy</h2>
          </div>
          <div className="divide-y divide-border">
            {/* Tagline */}
            <div className="px-6 py-5">
              <div className="mb-1.5 flex items-baseline justify-between">
                <label
                  htmlFor="tagline"
                  className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Tagline
                </label>
                <span className="num text-[10px] text-muted-foreground">
                  {form.tagline.length}/100
                </span>
              </div>
              <input
                id="tagline"
                type="text"
                value={form.tagline}
                onChange={(e) => {
                  setForm((f) => ({ ...f, tagline: e.target.value }));
                }}
                maxLength={100}
                placeholder="Where every dog gets the care they deserve."
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Short headline shown below your business name on the portal home page.
              </p>
            </div>

            {/* Pitch paragraph */}
            <div className="px-6 py-5">
              <div className="mb-1.5 flex items-baseline justify-between">
                <label
                  htmlFor="pitch-paragraph"
                  className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  About us
                </label>
                <span className="num text-[10px] text-muted-foreground">
                  {form.pitchParagraph.length}/400
                </span>
              </div>
              <textarea
                id="pitch-paragraph"
                value={form.pitchParagraph}
                onChange={(e) => {
                  setForm((f) => ({ ...f, pitchParagraph: e.target.value }));
                }}
                maxLength={400}
                rows={4}
                placeholder="A short paragraph about your kennel — your story, your approach to care, what makes you different."
                className="w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>
        </section>

        {/* Why us blocks */}
        <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-sm font-medium">Why choose us</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Up to 3 benefit blocks shown on the portal. Leave a block's title and body empty to
              hide it.
            </p>
          </div>
          <div className="divide-y divide-border">
            {form.whyUs.map((slot, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed 3-slot list with no reordering
              <div key={index} className="px-6 py-5">
                <p className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Block {index + 1}
                </p>
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor={`why-us-title-${index}`}
                      className="mb-1 block text-xs text-muted-foreground"
                    >
                      Title
                    </label>
                    <input
                      id={`why-us-title-${index}`}
                      type="text"
                      value={slot.title}
                      onChange={(e) => {
                        setWhyUsField(index, 'title', e.target.value);
                      }}
                      maxLength={80}
                      placeholder="e.g. Certified staff"
                      className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`why-us-body-${index}`}
                      className="mb-1 block text-xs text-muted-foreground"
                    >
                      Body
                    </label>
                    <input
                      id={`why-us-body-${index}`}
                      type="text"
                      value={slot.body}
                      onChange={(e) => {
                        setWhyUsField(index, 'body', e.target.value);
                      }}
                      maxLength={160}
                      placeholder="e.g. All our handlers hold Level 3 qualifications."
                      className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Image upload — sub-spec 07 Plan 3 */}
        <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-sm font-medium">Images</h2>
          </div>
          <div className="divide-y divide-border">
            {/* Logo */}
            <div className="px-6 py-5">
              <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Logo
              </p>
              {current?.logoR2Key && (
                <p className="mb-2 text-xs text-muted-foreground">A logo is currently set.</p>
              )}
              <FileUploader
                presignUrl="/api/branding/logo/presign"
                confirmUrl="/api/branding/logo/confirm"
                accept="image/png,image/svg+xml,image/x-icon"
                label={current?.logoR2Key ? 'Replace logo' : 'Upload logo'}
                onUploaded={() => {
                  // Refresh branding so the preview reflects the new key.
                  getBranding()
                    .then(setCurrent)
                    .catch(() => {});
                }}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">PNG, SVG, or ICO. Up to 1MB.</p>
            </div>

            {/* Hero image */}
            <div className="px-6 py-5">
              <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Hero image
              </p>
              {current?.heroImageR2Key && (
                <p className="mb-2 text-xs text-muted-foreground">A hero image is currently set.</p>
              )}
              <FileUploader
                presignUrl="/api/branding/hero/presign"
                confirmUrl="/api/branding/hero/confirm"
                accept="image/jpeg,image/png,image/webp"
                label={current?.heroImageR2Key ? 'Replace hero image' : 'Upload hero image'}
                onUploaded={() => {
                  getBranding()
                    .then(setCurrent)
                    .catch(() => {});
                }}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">JPEG, PNG, or WebP. Up to 5MB.</p>
            </div>
          </div>
        </section>

        {/* Feedback + actions */}
        <div className="rounded-2xl bg-surface ring-1 ring-border elevate px-6 py-5">
          {error !== null && <p className="mb-4 text-sm text-destructive">{error}</p>}
          {success && (
            <p className="mb-4 text-sm font-medium text-success">Branding updated successfully.</p>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!dirty || saving}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={!dirty || saving}
              className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground press disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
